import { useEffect, useMemo, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Box, Button, Flex, Grid, Text, TextField } from '@radix-ui/themes'
import { UploadIcon } from '@radix-ui/react-icons'
import { useDropzone } from 'react-dropzone'
import cn from 'classnames'
import { parseReading, readingValue } from '@willie/installation-guides'
import { useAuth } from '~/auth/AuthProvider'
import { FieldShell } from '~/components/FieldShell'
import { IMAGE_DROP_ACCEPT, IMAGE_MAX_BYTES } from '~/features/installations/constants'
import type { CaptureDetail } from '~/features/index-captures/alignment'
import { uploadIndexCapturePhoto } from '~/features/index-captures/service'
import { useTRPC } from '~/utils/trpc'

interface RecordReadingFormProps {
  detail: CaptureDetail
  onRecorded: () => Promise<void>
}

const toIso = (date: string, time: string): string | null => {
  if (!date || !time) return null
  const at = new Date(`${date}T${time}`)
  return Number.isNaN(at.getTime()) ? null : at.toISOString()
}

const usePreviewUrl = (file: File | null): string | null => {
  const url = useMemo(() => (file ? URL.createObjectURL(file) : null), [file])
  useEffect(() => () => (url ? URL.revokeObjectURL(url) : undefined), [url])
  return url
}

/** Answers the round on the contact's behalf, for a photo that reached us some other way. */
export const RecordReadingForm = ({ detail, onRecorded }: RecordReadingFormProps) => {
  const trpc = useTRPC()
  const { supabaseClient } = useAuth()
  const [file, setFile] = useState<File | null>(null)
  const [reading, setReading] = useState('')
  const [takenDate, setTakenDate] = useState('')
  const [takenTime, setTakenTime] = useState('')
  const [dropError, setDropError] = useState<string | null>(null)
  const previewUrl = usePreviewUrl(file)

  const recordReading = useMutation(trpc.ops.indexCaptures.recordReading.mutationOptions())
  const upload = useMutation({
    mutationFn: async (input: { file: File; readingValue: number; readingAt: string }) => {
      const { storagePath } = await uploadIndexCapturePhoto(supabaseClient, detail.installationId, input.file)
      await recordReading.mutateAsync({
        captureId: detail.id,
        storagePath,
        readingValue: input.readingValue,
        readingAt: input.readingAt
      })
      await onRecorded()
    }
  })

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: false,
    accept: IMAGE_DROP_ACCEPT,
    maxSize: IMAGE_MAX_BYTES,
    disabled: upload.isPending,
    onDropRejected: (rejections) => setDropError(rejections[0]?.errors[0]?.message ?? 'File not accepted'),
    onDrop: (accepted) => {
      if (!accepted[0]) return
      setDropError(null)
      setFile(accepted[0])
    }
  })

  const parsed = parseReading(reading)
  const typedValue = parsed.ok ? readingValue(parsed.digits) : null
  const readingAt = toIso(takenDate, takenTime)
  const canSubmit = file !== null && typedValue !== null && readingAt !== null && !upload.isPending

  return (
    <Flex direction="column" gap="3">
      <Text size="2" color="gray">
        Received the photo another way, e.g. by email? Upload it here with the index you read on it. The comparison
        runs as soon as you save.
      </Text>

      <Box
        {...getRootProps()}
        className={cn(
          'flex min-h-[10rem] cursor-pointer flex-col items-center justify-center rounded-[var(--radius-3)] border border-dashed p-3',
          isDragActive ? 'border-[color:var(--accent-9)] bg-[var(--accent-2)]' : 'border-[color:var(--gray-6)]'
        )}
      >
        <input {...getInputProps()} />
        {previewUrl ? (
          <img src={previewUrl} alt="The meter photo about to be uploaded" className="max-h-56 rounded-[var(--radius-2)]" />
        ) : (
          <Flex direction="column" align="center" gap="2">
            <UploadIcon width="24" height="24" color="var(--gray-9)" />
            <Text size="2" color="gray">
              {isDragActive ? 'Drop the photo here' : 'Drag the meter photo or click to choose it'}
            </Text>
          </Flex>
        )}
      </Box>
      {dropError ? (
        <Text size="1" color="red">
          {dropError}
        </Text>
      ) : null}

      <Grid columns={{ initial: '1', sm: '3' }} gap="3">
        <FieldShell label="Index read on the photo (m³)">
          <TextField.Root
            inputMode="decimal"
            placeholder="e.g. 1234,567"
            value={reading}
            onChange={(event) => setReading(event.target.value)}
          />
        </FieldShell>
        <FieldShell label="Photo taken on">
          <TextField.Root type="date" value={takenDate} onChange={(event) => setTakenDate(event.target.value)} />
        </FieldShell>
        <FieldShell label="At">
          <TextField.Root type="time" value={takenTime} onChange={(event) => setTakenTime(event.target.value)} />
        </FieldShell>
      </Grid>
      <Text size="1" color="gray">
        When the photo was taken, not when it arrived: the comparison measures telemetry up to that moment.
      </Text>

      {upload.error ? (
        <Text size="2" color="red">
          {upload.error.message}
        </Text>
      ) : null}

      <Flex justify="end">
        <Button
          disabled={!canSubmit}
          loading={upload.isPending}
          onClick={() => {
            if (!file || typedValue === null || !readingAt) return
            upload.mutate({ file, readingValue: typedValue, readingAt })
          }}
        >
          Save and compare
        </Button>
      </Flex>
    </Flex>
  )
}
